import type { VersionedData } from '../shared/VersionedData';

export type PhilosophyEntryType = 'original' | 'classical';

export interface PhilosophyContent {
  readonly text: string;
  readonly origin: string;
  readonly type: PhilosophyEntryType;
  /** Verifiable source for a classical quotation; explicitly absent otherwise. */
  readonly source?: string;
  readonly explanation: string;
  readonly challenge: string;
}

const CONTENT_VERSION = 1;

export function toPhilosophyContentData(content: PhilosophyContent): VersionedData {
  return { version: CONTENT_VERSION, payload: { ...content } };
}

function isEntryType(value: unknown): value is PhilosophyEntryType {
  return value === 'original' || value === 'classical';
}

/** Returns undefined for missing, malformed or future-versioned payloads rather than throwing. */
export function fromPhilosophyContentData(
  data: VersionedData | undefined,
): PhilosophyContent | undefined {
  if (!data || data.version !== CONTENT_VERSION) return undefined;
  const { text, origin, type, source, explanation, challenge } = data.payload;
  if (
    typeof text !== 'string' ||
    typeof origin !== 'string' ||
    !isEntryType(type) ||
    typeof explanation !== 'string' ||
    typeof challenge !== 'string'
  ) {
    return undefined;
  }
  return {
    text,
    origin,
    type,
    explanation,
    challenge,
    ...(typeof source === 'string' ? { source } : {}),
  };
}
